import api from './client'
import { APP_BASE } from '@/config/basePath'

// URL of the WildFly SSO bridge servlet that mints a short-lived HMAC token for
// the already-authenticated /ibase user. Defaults to the Twasta WAR's own
// endpoint; override with VITE_SSO_BRIDGE_URL when the session is owned by a
// different WAR (e.g. '/ibase/sso/token', which is what deploy/wildfly/
// install-spa-into-ibase.sh sets — ibase.war owns the session).
const SSO_BRIDGE_URL =
  (import.meta.env.VITE_SSO_BRIDGE_URL as string | undefined) || `${APP_BASE}/sso/token`

// sessionStorage marker set on logout so the immediate next mount does NOT
// auto-SSO straight back in (full logout happens in the ibase app). Cleared
// when the user explicitly clicks "Sign in" on the login page.
const SSO_SUPPRESSED_KEY = 'sso_suppressed'

export function markSsoSuppressed() {
  try { sessionStorage.setItem(SSO_SUPPRESSED_KEY, '1') } catch { /* ignore */ }
}
export function clearSsoSuppressed() {
  try { sessionStorage.removeItem(SSO_SUPPRESSED_KEY) } catch { /* ignore */ }
}
export function isSsoSuppressed(): boolean {
  try { return sessionStorage.getItem(SSO_SUPPRESSED_KEY) === '1' } catch { return false }
}

export interface LoginPayload {
  email: string
  password: string
}

export interface ConfirmLoginPayload {
  email: string
  password: string
  user_choice: 'terminate' | 'cancel'
}

export interface RegisterPayload {
  email: string
  username: string
  password: string
  confirm_password: string
  organization?: string
  invitation_token?: string
  captcha_id?: string
  captcha_answer?: string
  /** 2-letter ISO code. Decides the checkout currency and the invoice tax rule. */
  country?: string
}

export interface ActivationPayload {
  email: string
  activation_code: string
  /** Only for admin-granted accounts (must_set_password): the chosen password. */
  password?: string
  confirm_password?: string
}

export interface ForgotPasswordPayload {
  email: string
}

export interface ResetPasswordPayload {
  password: string
  confirm_password: string
}

export interface WaitlistPayload {
  name: string
  email: string
  organization?: string
  application_idea?: string
  /** 2-letter ISO code; becomes the granted account's billing country. */
  country?: string
}

export async function login(payload: LoginPayload) {
  const { data } = await api.post('/auth/login', payload)
  return data
}

/* ── Two-factor sign-in (a password sign-in parked on `mfa_token`) ──────── */

/** Finish signing in with an authenticator or recovery code. */
export async function mfaVerify(mfaToken: string, code: string) {
  const { data } = await api.post('/auth/mfa/verify', { mfa_token: mfaToken, code })
  return data
}

/** Set up an authenticator during a sign-in that requires one. */
export async function mfaEnrolStart(mfaToken: string) {
  const { data } = await api.post('/auth/mfa/enrol/start', { mfa_token: mfaToken })
  return data as { secret: string; otpauth_uri: string; qr_svg?: string | null; account?: string }
}

export async function mfaEnrolConfirm(mfaToken: string, code: string) {
  const { data } = await api.post('/auth/mfa/enrol/confirm', { mfa_token: mfaToken, code })
  return data as { redirect?: string; recovery_codes: string[]; password_change_required?: boolean }
}

export async function confirmLoginChoice(payload: ConfirmLoginPayload) {
  const { data } = await api.post('/auth/confirm-login-choice', payload)
  return data
}

export async function register(payload: RegisterPayload) {
  const { data } = await api.post('/auth/register', payload)
  return data
}

export async function activate(payload: ActivationPayload) {
  const { data } = await api.post('/auth/activate', payload)
  return data
}

export async function activateAccount(email: string, code: string) {
  const { data } = await api.get('/auth/activate-account', {
    params: { email, code },
  })
  return data
}

export async function resendActivation(email: string) {
  const { data } = await api.post('/auth/resend-activation', { email })
  return data
}

export async function forgotPassword(payload: ForgotPasswordPayload) {
  const { data } = await api.post('/auth/forgot-password', payload)
  return data
}

export async function resetPassword(token: string, payload: ResetPasswordPayload) {
  const { data } = await api.post(`/auth/reset-password/${token}`, payload)
  return data
}

export async function logout() {
  // Suppress an immediate auto-SSO on the next mount (the user just logged out;
  // a full logout including the ibase session happens in the ibase app).
  markSsoSuppressed()
  const { data } = await api.post('/auth/logout')
  return data
}

// Exchange the bridge token for a real Twasta session (httpOnly cookies + the
// /me-shaped payload). Goes through the axios client so it shares baseURL/cookie
// config; '/auth/sso' is in SKIP_REFRESH_URLS so a 401 won't trigger the
// refresh interceptor.
export async function ssoLogin(token: string) {
  const { data } = await api.post('/auth/sso', { token })
  return data
}

// Ask the WildFly bridge for a fresh token for the current ibase session.
// Raw fetch (not the axios client) on purpose so the 401 response interceptor
// never fires when there is no ibase session. Returns the token string, or
// throws if there is no authenticated session / no bridge.
export async function fetchBridgeToken(): Promise<string> {
  const res = await fetch(SSO_BRIDGE_URL, { credentials: 'include' })
  if (!res.ok) throw new Error(`bridge token unavailable (${res.status})`)
  const data = await res.json()
  if (!data?.token) throw new Error('bridge response missing token')
  return data.token as string
}

export async function getMe() {
  const { data } = await api.get('/auth/me')
  return data
}

export async function getCaptcha() {
  const { data } = await api.get('/auth/captcha')
  return data
}

export async function joinWaitlist(payload: WaitlistPayload) {
  const { data } = await api.post('/auth/waitlist', payload)
  return data
}

export async function getCurrentPlan() {
  const { data } = await api.get('/auth/current-plan')
  return data
}

export async function changePlan(plan: string) {
  const { data } = await api.post('/auth/change-plan', { plan })
  return data
}

export async function verifySubscription(paymentData: {
  razorpay_payment_id: string
  razorpay_subscription_id: string
  razorpay_signature: string
}) {
  const { data } = await api.post('/auth/verify-subscription', paymentData)
  return data
}

export async function cancelSubscription() {
  const { data } = await api.post('/auth/cancel-subscription')
  return data
}
